/**
 * LandingPage.ts — Public marketing homepage for preuvu.com
 * Shown to unauthenticated visitors in web mode.
 */

type NavigateFn = (route: string, params?: Record<string, string>) => void;

export function renderLandingPage(container: HTMLElement, navigate: NavigateFn): void {
  container.innerHTML = `
<div class="lp-root">

  <!-- ── Sticky Nav ──────────────────────────────────────────────────────── -->
  <nav class="lp-nav" id="lp-nav">
    <div class="lp-nav-inner">
      <a class="lp-logo" href="#top">✦ <span>PREUVU</span></a>
      <div class="lp-nav-links">
        <a href="#how-it-works">How it works</a>
        <a href="#features">Features</a>
        <a href="#pricing">Pricing</a>
        <a href="#faq">FAQ</a>
        <a href="/downloads/">Downloads</a>
      </div>
      <div class="lp-nav-actions">
        <button class="lp-btn-ghost" id="nav-signin">Sign In</button>
        <button class="lp-btn-gold" id="nav-get-started">Get Started Free</button>
      </div>
      <button class="lp-nav-hamburger" id="lp-hamburger" aria-label="Menu">&#9776;</button>
    </div>
  </nav>

  <!-- ── Mobile nav drawer ─────────────────────────────────────────────── -->
  <div class="lp-mobile-drawer" id="lp-drawer">
    <button class="lp-drawer-close" id="lp-drawer-close">&#10005;</button>
    <a href="#how-it-works" class="lp-drawer-link">How it works</a>
    <a href="#features" class="lp-drawer-link">Features</a>
    <a href="#pricing" class="lp-drawer-link">Pricing</a>
    <a href="#faq" class="lp-drawer-link">FAQ</a>
    <a href="/downloads/" class="lp-drawer-link">Downloads</a>
    <button class="lp-btn-gold lp-drawer-cta" id="drawer-get-started">Get Started Free</button>
    <button class="lp-btn-ghost lp-drawer-signin" id="drawer-signin">Sign In</button>
  </div>
  <div class="lp-drawer-overlay" id="lp-drawer-overlay"></div>

  <!-- ── Hero ───────────────────────────────────────────────────────────── -->
  <section class="lp-hero" id="top">
    <div class="lp-hero-content">
      <h1 class="lp-hero-h1">AI Content Compliance,<br><span class="lp-gold">Proven in Court.</span></h1>
      <div class="lp-hero-badges">
        <span class="lp-hero-badge"><i class="bi bi-shield-check"></i> WA SB&nbsp;5838</span>
        <span class="lp-hero-badge"><i class="bi bi-patch-check"></i> EU AI Act</span>
        <span class="lp-hero-badge"><i class="bi bi-lock"></i> AES-256</span>
      </div>
      <p class="lp-hero-sub">
        PREUVU is a Digital Notary platform that certifies, timestamps, and cryptographically signs
        every AI-generated document your business produces — so you can prove compliance with the
        EU&nbsp;AI&nbsp;Act, WA&nbsp;SB&nbsp;5838, and any regulation that requires AI disclosure.
      </p>
      <div class="lp-hero-actions">
        <button class="lp-btn-gold lp-btn-xl" id="hero-cta">Start Your Free Trial</button>
        <button class="lp-btn-outline lp-btn-xl" id="hero-verify">Verify a Certificate →</button>
      </div>
      <div class="lp-hero-trust">
        <span><i class="bi bi-patch-check-fill" style="color:#d4a017"></i> Cryptographically signed</span>
        <span><i class="bi bi-lock-fill" style="color:#d4a017"></i> Tamper-evident</span>
        <span><i class="bi bi-clock-fill" style="color:#d4a017"></i> Immutable timestamps</span>
        <span><i class="bi bi-shield-fill-check" style="color:#d4a017"></i> Audit-ready</span>
      </div>
    </div>
    <div class="lp-hero-visual">
      <div class="lp-mockup">
        <div class="lp-mockup-bar">
          <span class="lp-mockup-dot"></span><span class="lp-mockup-dot"></span><span class="lp-mockup-dot"></span>
          <span class="lp-mockup-url">preuvu.com</span>
        </div>
        <div class="lp-mockup-body">
          <div class="lp-mockup-cert">
            <div class="lp-mockup-cert-icon">◈</div>
            <div class="lp-mockup-cert-title">Menu Description — Salmon</div>
            <div class="lp-mockup-cert-id">CERT-A3F9B2C1-D4E7…</div>
            <div class="lp-mockup-cert-row"><span class="lp-mockup-label">Model</span><span class="lp-mockup-val">claude-sonnet-4-6</span></div>
            <div class="lp-mockup-cert-row"><span class="lp-mockup-label">Human edits</span><span class="lp-mockup-val">12%</span></div>
            <div class="lp-mockup-cert-row"><span class="lp-mockup-label">Timestamp</span><span class="lp-mockup-val">2026-05-27 14:32 UTC</span></div>
            <div class="lp-mockup-cert-row"><span class="lp-mockup-label">Status</span><span class="lp-mockup-cert-valid">✓ Valid</span></div>
          </div>
          <div class="lp-mockup-sessions">
            <div class="lp-mockup-session-item"><i class="bi bi-stars" style="color:#d4a017"></i> Contract Clause Draft <span class="lp-cert-badge">certified</span></div>
            <div class="lp-mockup-session-item"><i class="bi bi-stars" style="color:#d4a017"></i> Insurance Summary <span class="lp-cert-badge">certified</span></div>
            <div class="lp-mockup-session-item"><i class="bi bi-stars" style="color:#d4a017"></i> Menu Copy — June 2026 <span class="lp-draft-badge">draft</span></div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Compliance strip ────────────────────────────────────────────────── -->
  <section class="lp-compliance-strip">
    <div class="lp-strip-inner">
      <div class="lp-strip-label">Built to satisfy</div>
      <div class="lp-strip-badges">
        <div class="lp-reg-badge"><strong>EU AI Act</strong><span>Art. 52 Transparency</span></div>
        <div class="lp-reg-badge"><strong>WA SB 5838</strong><span>AI Disclosure</span></div>
        <div class="lp-reg-badge"><strong>CCPA / GDPR</strong><span>Data Privacy</span></div>
        <div class="lp-reg-badge"><strong>FTC Guidelines</strong><span>AI Disclosure</span></div>
        <div class="lp-reg-badge"><strong>SOC 2 Ready</strong><span>Audit Trail</span></div>
      </div>
    </div>
  </section>

  <!-- ── How it works ────────────────────────────────────────────────────── -->
  <section class="lp-section" id="how-it-works">
    <div class="lp-section-inner">
      <div class="lp-section-badge">Simple Process</div>
      <h2 class="lp-section-h2">From AI prompt to legal proof <span class="lp-gold">in 3 steps</span></h2>
      <p class="lp-section-sub">No legal background needed. PREUVU handles the cryptographic signing, timestamping, and record-keeping automatically.</p>

      <div class="lp-steps">
        <div class="lp-step">
          <div class="lp-step-num">1</div>
          <div class="lp-step-icon"><i class="bi bi-pencil-square"></i></div>
          <h3>Write your prompt</h3>
          <p>Type what you need — a menu description, contract clause, insurance summary, or any business document. Your prompt is hashed and sealed the moment you send it.</p>
        </div>
        <div class="lp-step-arrow"><i class="bi bi-arrow-right"></i></div>
        <div class="lp-step">
          <div class="lp-step-num">2</div>
          <div class="lp-step-icon"><i class="bi bi-pencil"></i></div>
          <h3>Edit the AI output</h3>
          <p>Make any changes you need. Every keystroke is tracked. PREUVU records exactly what the AI originally wrote and exactly what percentage a human modified.</p>
        </div>
        <div class="lp-step-arrow"><i class="bi bi-arrow-right"></i></div>
        <div class="lp-step">
          <div class="lp-step-num">3</div>
          <div class="lp-step-icon"><i class="bi bi-patch-check"></i></div>
          <h3>Certify &amp; lock</h3>
          <p>One click generates a tamper-evident certificate with a unique ID. Download the PDF, share the link, or present it directly to a regulator, judge, or client.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Industry features ───────────────────────────────────────────────── -->
  <section class="lp-section lp-section-dark" id="features">
    <div class="lp-section-inner">
      <div class="lp-section-badge">Built for Your Industry</div>
      <h2 class="lp-section-h2">Everything your business needs, <span class="lp-gold">nothing it doesn't</span></h2>

      <div class="lp-industry-tabs" id="lp-industry-tabs">
        <button class="lp-tab active" data-tab="general">💼 General</button>
        <button class="lp-tab" data-tab="restaurant">🍽 Restaurant</button>
        <button class="lp-tab" data-tab="construction">🏗 Construction</button>
      </div>

      <div class="lp-tab-panels">
        <!-- General -->
        <div class="lp-tab-panel active" id="tab-general">
          <div class="lp-features-grid">
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-stars"></i></div>
              <h4>AI Compliance Sessions</h4>
              <p>Generate AI content through Claude, GPT, or free HuggingFace models — every interaction is logged, hashed, and immutable.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-patch-check"></i></div>
              <h4>Digital Certificates</h4>
              <p>Cryptographically signed PDF certificates for every certified document. Verifiable by anyone at any time at preuvu.com/verify.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-file-earmark-text"></i></div>
              <h4>Template Library</h4>
              <p>Pre-built compliance templates for common business documents. Fill, sign, and certify in minutes.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-people"></i></div>
              <h4>Customer &amp; Employee CRM</h4>
              <p>Manage customers, employees, and their documents. Everything linked to your compliance audit trail.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-graph-up"></i></div>
              <h4>Business Analytics</h4>
              <p>Revenue tracking, session history, and compliance reporting — presented in clean, exportable dashboards.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-camera2"></i></div>
              <h4>Document Scanner</h4>
              <p>Scan physical documents into your audit trail. Multi-page PDF extraction with text recognition.</p>
            </div>
          </div>
        </div>

        <!-- Restaurant -->
        <div class="lp-tab-panel" id="tab-restaurant">
          <div class="lp-features-grid">
            <div class="lp-feature-card lp-feature-card-highlight">
              <div class="lp-feature-icon"><i class="bi bi-menu-button-wide"></i></div>
              <h4>Menu Management</h4>
              <p>Build and publish menus with AI-generated descriptions — every AI-written item automatically gets a compliance certificate.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-tablet"></i></div>
              <h4>Tablet Ordering</h4>
              <p>Guest-facing tablet ordering system. Orders flow directly to the kitchen display with no paper tickets.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-fire"></i></div>
              <h4>Kitchen Display System</h4>
              <p>Real-time order queue for the kitchen. Color-coded priority, prep-time tracking, and one-tap completion.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-grid"></i></div>
              <h4>Table Management</h4>
              <p>Interactive floor plan with real-time table status, party size, and turn-time tracking.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-calendar3"></i></div>
              <h4>Staff Scheduling</h4>
              <p>Drag-and-drop work schedule builder with shift conflict detection and automatic payroll export.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-cash-stack"></i></div>
              <h4>POS &amp; Revenue Tracking</h4>
              <p>Track daily revenue, tips, voids, and refunds. Reconcile with payroll in one click.</p>
            </div>
          </div>
        </div>

        <!-- Construction -->
        <div class="lp-tab-panel" id="tab-construction">
          <div class="lp-features-grid">
            <div class="lp-feature-card lp-feature-card-highlight">
              <div class="lp-feature-icon"><i class="bi bi-shield-check"></i></div>
              <h4>WA SB 5838 Compliance</h4>
              <p>Auto-generate the required AI disclosure reports for Washington State construction projects. One-click export for regulatory filing.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-receipt"></i></div>
              <h4>Invoice Management</h4>
              <p>Create, track, and certify construction invoices. Every AI-drafted line item is logged in the audit trail.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-coin"></i></div>
              <h4>Project Financials</h4>
              <p>Estimate vs. actual cost tracking per project. Change order management with digital sign-off.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-camera2"></i></div>
              <h4>Document Scanner</h4>
              <p>Scan inspection reports, permits, and contracts into your digital file — searchable and tamper-evident.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-person-badge"></i></div>
              <h4>Employee &amp; Subcontractor Mgmt</h4>
              <p>Manage workers, certifications, and access levels. Tie every AI-drafted document to the person who approved it.</p>
            </div>
            <div class="lp-feature-card">
              <div class="lp-feature-icon"><i class="bi bi-cash-stack"></i></div>
              <h4>Payroll Integration</h4>
              <p>Track hours, overtime, and prevailing-wage rates. Export to payroll providers in one click.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Social proof / stats ────────────────────────────────────────────── -->
  <section class="lp-section lp-section-stats">
    <div class="lp-section-inner">
      <div class="lp-stats-grid">
        <div class="lp-stat">
          <div class="lp-stat-num">100%</div>
          <div class="lp-stat-label">Cryptographically verifiable</div>
        </div>
        <div class="lp-stat">
          <div class="lp-stat-num">&lt; 2s</div>
          <div class="lp-stat-label">Certificate generation time</div>
        </div>
        <div class="lp-stat">
          <div class="lp-stat-num">3</div>
          <div class="lp-stat-label">AI providers supported</div>
        </div>
        <div class="lp-stat">
          <div class="lp-stat-num">∞</div>
          <div class="lp-stat-label">Certificate verifications (free)</div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Pricing ────────────────────────────────────────────────────────── -->
  <section class="lp-section" id="pricing">
    <div class="lp-section-inner">
      <div class="lp-section-badge">Simple Pricing</div>
      <h2 class="lp-section-h2">Start free, <span class="lp-gold">scale as you grow</span></h2>
      <p class="lp-section-sub">All plans include a 14-day free trial. No credit card required to start.</p>

      <div class="lp-billing-toggle">
        <button class="lp-toggle-btn active" id="toggle-monthly" data-billing="monthly">Monthly</button>
        <button class="lp-toggle-btn" id="toggle-annual" data-billing="annual">Annual <span class="lp-save-badge">Save 17%</span></button>
      </div>

      <div class="lp-pricing-grid">
        <!-- Starter -->
        <div class="lp-pricing-card">
          <div class="lp-plan-name">Starter</div>
          <div class="lp-plan-for">General business compliance</div>
          <div class="lp-plan-price">
            <span class="lp-price-monthly lp-active">$79</span>
            <span class="lp-price-annual">$69</span>
            <span class="lp-per">/mo</span>
          </div>
          <div class="lp-plan-billed lp-billed-annual" style="display:none">Billed $828/year</div>
          <ul class="lp-plan-features">
            <li><i class="bi bi-check-lg"></i> 50 AI sessions / month</li>
            <li><i class="bi bi-check-lg"></i> Digital PDF certificates</li>
            <li><i class="bi bi-check-lg"></i> Audit trail</li>
            <li><i class="bi bi-check-lg"></i> Template library</li>
            <li><i class="bi bi-check-lg"></i> 1 user seat</li>
            <li><i class="bi bi-check-lg"></i> Email support</li>
          </ul>
          <button class="lp-plan-btn" data-plan="starter">Start Free Trial</button>
        </div>

        <!-- Restaurant (highlighted) -->
        <div class="lp-pricing-card lp-pricing-featured">
          <div class="lp-featured-badge">Most Popular</div>
          <div class="lp-plan-name">Restaurant Suite</div>
          <div class="lp-plan-for">Full restaurant management + compliance</div>
          <div class="lp-plan-price">
            <span class="lp-price-monthly lp-active">$99</span>
            <span class="lp-price-annual">$82</span>
            <span class="lp-per">/mo</span>
          </div>
          <div class="lp-plan-billed lp-billed-annual" style="display:none">Billed $984/year</div>
          <ul class="lp-plan-features">
            <li><i class="bi bi-check-lg"></i> Everything in Starter</li>
            <li><i class="bi bi-check-lg"></i> POS &amp; revenue tracking</li>
            <li><i class="bi bi-check-lg"></i> Menu + tablet ordering</li>
            <li><i class="bi bi-check-lg"></i> Kitchen display system</li>
            <li><i class="bi bi-check-lg"></i> Table management</li>
            <li><i class="bi bi-check-lg"></i> Staff scheduling &amp; payroll</li>
            <li><i class="bi bi-check-lg"></i> 20 user seats</li>
            <li><i class="bi bi-check-lg"></i> Priority support</li>
          </ul>
          <button class="lp-plan-btn lp-plan-btn-gold" data-plan="restaurant">Start Free Trial</button>
        </div>

        <!-- Construction -->
        <div class="lp-pricing-card">
          <div class="lp-plan-name">Construction Suite</div>
          <div class="lp-plan-for">WA compliance + invoicing</div>
          <div class="lp-plan-price">
            <span class="lp-price-monthly lp-active">$149</span>
            <span class="lp-price-annual">$124</span>
            <span class="lp-per">/mo</span>
          </div>
          <div class="lp-plan-billed lp-billed-annual" style="display:none">Billed $1,488/year</div>
          <ul class="lp-plan-features">
            <li><i class="bi bi-check-lg"></i> Everything in Starter</li>
            <li><i class="bi bi-check-lg"></i> WA SB 5838 compliance</li>
            <li><i class="bi bi-check-lg"></i> Invoice management</li>
            <li><i class="bi bi-check-lg"></i> Project financials</li>
            <li><i class="bi bi-check-lg"></i> Document scanner (PDF)</li>
            <li><i class="bi bi-check-lg"></i> Payroll + employee mgmt</li>
            <li><i class="bi bi-check-lg"></i> 5 user seats</li>
            <li><i class="bi bi-check-lg"></i> Dedicated support</li>
          </ul>
          <button class="lp-plan-btn" data-plan="construction">Start Free Trial</button>
        </div>

        <!-- Enterprise -->
        <div class="lp-pricing-card">
          <div class="lp-plan-name">Enterprise</div>
          <div class="lp-plan-for">Custom volume · SSO · white-label</div>
          <div class="lp-plan-price">
            <span style="font-size:28px;font-weight:800">Custom</span>
          </div>
          <ul class="lp-plan-features">
            <li><i class="bi bi-check-lg"></i> Unlimited sessions &amp; seats</li>
            <li><i class="bi bi-check-lg"></i> SSO / SAML integration</li>
            <li><i class="bi bi-check-lg"></i> White-label branding</li>
            <li><i class="bi bi-check-lg"></i> API access</li>
            <li><i class="bi bi-check-lg"></i> Dedicated account manager</li>
            <li><i class="bi bi-check-lg"></i> Custom SLA &amp; uptime</li>
          </ul>
          <button class="lp-plan-btn lp-plan-btn-outline" id="enterprise-contact">Contact Sales</button>
        </div>
      </div>

      <p class="lp-pricing-note">All plans include a 14-day free trial · Cancel anytime · Verify certificates always free</p>
    </div>
  </section>

  <!-- ── FAQ ────────────────────────────────────────────────────────────── -->
  <section class="lp-section lp-section-dark" id="faq">
    <div class="lp-section-inner lp-faq-inner">
      <div class="lp-section-badge">FAQ</div>
      <h2 class="lp-section-h2">Common <span class="lp-gold">questions</span></h2>

      <div class="lp-faq-list" id="lp-faq">
        ${FAQ.map((f, i) => `
        <div class="lp-faq-item">
          <button class="lp-faq-q" data-faq="${i}">
            ${f.q}
            <i class="bi bi-chevron-down lp-faq-chevron"></i>
          </button>
          <div class="lp-faq-a" id="faq-a-${i}" style="display:none">${f.a}</div>
        </div>`).join('')}
      </div>
    </div>
  </section>

  <!-- ── CTA banner ──────────────────────────────────────────────────────── -->
  <section class="lp-cta-banner">
    <div class="lp-section-inner lp-cta-inner">
      <h2 class="lp-cta-h2">Ready to prove compliance<br><span class="lp-gold">before you need to?</span></h2>
      <p class="lp-cta-sub">Join businesses that are already building audit-ready AI workflows.<br>14-day free trial. No credit card required.</p>
      <div class="lp-cta-actions">
        <button class="lp-btn-gold lp-btn-xl" id="cta-get-started">Get Started Free</button>
        <button class="lp-btn-outline lp-btn-xl" id="cta-verify">Verify a Certificate</button>
      </div>
    </div>
  </section>

  <!-- ── Footer ─────────────────────────────────────────────────────────── -->
  <footer class="lp-footer">
    <div class="lp-footer-inner">
      <div class="lp-footer-brand">
        <div class="lp-footer-logo">✦ <span class="lp-gold">PREUVU</span></div>
        <p class="lp-footer-tagline">Digital Notary for AI Content Compliance</p>
        <p class="lp-footer-copy">© ${new Date().getFullYear()} PREUVU. All rights reserved.</p>
      </div>
      <div class="lp-footer-cols">
        <div class="lp-footer-col">
          <div class="lp-footer-col-title">Product</div>
          <a href="#how-it-works">How it works</a>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#" class="lp-footer-verify">Verify a Certificate</a>
        </div>
        <div class="lp-footer-col">
          <div class="lp-footer-col-title">Industries</div>
          <a href="#features">General Business</a>
          <a href="#features">Restaurant</a>
          <a href="#features">Construction</a>
          <a href="#pricing">Enterprise</a>
        </div>
        <div class="lp-footer-col">
          <div class="lp-footer-col-title">Downloads</div>
          <a href="/downloads/PREUVU.Setup.1.0.0.exe" class="lp-dl-link">
            <i class="bi bi-windows"></i> Windows
            <span class="lp-dl-badge">v1.0.0</span>
          </a>
          <a href="/downloads/" class="lp-dl-link lp-dl-soon">
            <i class="bi bi-apple"></i> macOS
            <span class="lp-dl-soon-tag">Soon</span>
          </a>
          <a href="/downloads/" class="lp-dl-link lp-dl-soon">
            <i class="bi bi-phone"></i> iOS
            <span class="lp-dl-soon-tag">Soon</span>
          </a>
          <a href="/downloads/" class="lp-dl-link lp-dl-soon">
            <i class="bi bi-android2"></i> Android
            <span class="lp-dl-soon-tag">Soon</span>
          </a>
        </div>
        <div class="lp-footer-col">
          <div class="lp-footer-col-title">Legal</div>
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms of Service</a>
          <a href="mailto:support@preuvu.com">Contact Us</a>
          <a href="mailto:legal@preuvu.com">Legal Inquiries</a>
        </div>
      </div>
    </div>
    <div class="lp-footer-bottom">
      PREUVU is not a law firm. Certificates provide a cryptographic record — consult legal counsel for regulatory guidance.
    </div>
  </footer>

</div>
  `;

  wireLandingPage(navigate);
}

// ─── FAQ content ──────────────────────────────────────────────────────────────

const FAQ = [
  {
    q: 'What is PREUVU and why do I need it?',
    a: 'PREUVU is a Digital Notary platform for AI-generated content. Laws like the EU AI Act and Washington State SB 5838 require businesses to disclose when content was AI-generated. PREUVU creates a cryptographically signed, timestamped record of every AI interaction — so if a regulator, judge, or client ever asks "did AI write this?", you have undeniable proof of exactly what was AI-written, what was human-edited, and when.',
  },
  {
    q: 'How does the certificate verification work?',
    a: 'Each certificate has a unique ID. Anyone — including regulators, auditors, or clients — can visit <strong>preuvu.com</strong> and enter the certificate ID to verify its authenticity. The verification is free, requires no account, and cannot be spoofed because the certificate is cryptographically signed with a private key that only PREUVU holds.',
  },
  {
    q: 'Which AI models can I use?',
    a: 'PREUVU works with any AI provider: Anthropic (Claude), OpenAI (GPT-4o), or free HuggingFace models (Mistral, Llama, Qwen, Phi). Your own API keys are used — PREUVU never stores your AI responses in a way that could be read by our team. We only store the cryptographic hash and metadata needed for the certificate.',
  },
  {
    q: 'Is my content private?',
    a: 'Yes. We store the minimum data needed to issue and verify the certificate: a SHA-256 hash of the content, the AI model used, the timestamp, and the human-edit percentage. The full prompt and response are stored encrypted and are only accessible by your account. You can delete your data at any time from Settings.',
  },
  {
    q: 'What is the "human edit percentage"?',
    a: 'When the AI generates a response, PREUVU records the original text. As you edit it, we calculate what percentage of the final document was changed from the original AI output. For example, 0% means you published the AI\'s exact output; 100% means you rewrote everything. This metric is included in the certificate because some regulations require disclosing the degree of human involvement.',
  },
  {
    q: 'Can I use PREUVU on mobile?',
    a: 'Yes. The web app at preuvu.com is fully responsive and works on any browser. Native iOS and Android apps are in development.',
  },
  {
    q: 'Do I need a credit card to try it?',
    a: 'No. All plans come with a 14-day free trial and no credit card is required to start. You only need to add billing when you\'re ready to continue after the trial.',
  },
  {
    q: 'What happens if I cancel?',
    a: 'You can cancel anytime from your billing settings. Your existing certificates remain valid and publicly verifiable forever — we do not delete issued certificates even if your subscription ends. Your account data is held for 30 days before deletion so you can export it.',
  },
];

// ─── Wire interactions ────────────────────────────────────────────────────────

function wireLandingPage(navigate: NavigateFn): void {
  // ── Nav CTA buttons
  const goRegister = (): void => navigate('login');
  const goVerify   = (): void => navigate('verify');

  document.getElementById('nav-signin')?.addEventListener('click', goRegister);
  document.getElementById('nav-get-started')?.addEventListener('click', goRegister);
  document.getElementById('hero-cta')?.addEventListener('click', goRegister);
  document.getElementById('hero-verify')?.addEventListener('click', goVerify);
  document.getElementById('cta-get-started')?.addEventListener('click', goRegister);
  document.getElementById('cta-verify')?.addEventListener('click', goVerify);
  document.getElementById('drawer-get-started')?.addEventListener('click', goRegister);
  document.getElementById('drawer-signin')?.addEventListener('click', goRegister);
  document.querySelectorAll('.lp-footer-verify').forEach(el => el.addEventListener('click', (e) => { e.preventDefault(); goVerify(); }));

  // ── Pricing plan buttons
  document.querySelectorAll('.lp-plan-btn[data-plan]').forEach(btn => {
    btn.addEventListener('click', () => goRegister());
  });
  document.getElementById('enterprise-contact')?.addEventListener('click', () => {
    window.location.href = 'mailto:sales@preuvu.com?subject=Enterprise%20Plan%20Inquiry';
  });

  // ── Mobile drawer
  const drawerEl  = document.getElementById('lp-drawer');
  const overlayEl = document.getElementById('lp-drawer-overlay');
  const openDrawer  = (): void => { drawerEl?.classList.add('open'); overlayEl?.classList.add('open'); };
  const closeDrawer = (): void => { drawerEl?.classList.remove('open'); overlayEl?.classList.remove('open'); };
  document.getElementById('lp-hamburger')?.addEventListener('click', openDrawer);
  document.getElementById('lp-drawer-close')?.addEventListener('click', closeDrawer);
  overlayEl?.addEventListener('click', closeDrawer);
  drawerEl?.querySelectorAll('.lp-drawer-link').forEach(a => a.addEventListener('click', closeDrawer));

  // ── Sticky nav shadow on scroll
  const nav = document.getElementById('lp-nav');
  const handleScroll = (): void => {
    if (window.scrollY > 20) nav?.classList.add('scrolled');
    else nav?.classList.remove('scrolled');
  };
  window.addEventListener('scroll', handleScroll, { passive: true });

  // ── Industry tabs
  document.querySelectorAll('.lp-tab[data-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      const tab = btn.getAttribute('data-tab')!;
      document.querySelectorAll('.lp-tab').forEach(t => t.classList.remove('active'));
      document.querySelectorAll('.lp-tab-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      document.getElementById(`tab-${tab}`)?.classList.add('active');
    });
  });

  // ── Billing toggle
  document.querySelectorAll('.lp-toggle-btn[data-billing]').forEach(btn => {
    btn.addEventListener('click', () => {
      const billing = btn.getAttribute('data-billing')!;
      document.querySelectorAll('.lp-toggle-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const isAnnual = billing === 'annual';
      document.querySelectorAll('.lp-price-monthly').forEach(el => {
        (el as HTMLElement).style.display = isAnnual ? 'none' : '';
        el.classList.toggle('lp-active', !isAnnual);
      });
      document.querySelectorAll('.lp-price-annual').forEach(el => {
        (el as HTMLElement).style.display = isAnnual ? '' : 'none';
        el.classList.toggle('lp-active', isAnnual);
      });
      document.querySelectorAll('.lp-billed-annual').forEach(el => {
        (el as HTMLElement).style.display = isAnnual ? '' : 'none';
      });
    });
  });

  // ── FAQ accordion
  document.querySelectorAll('.lp-faq-q[data-faq]').forEach(btn => {
    btn.addEventListener('click', () => {
      const idx   = btn.getAttribute('data-faq')!;
      const ansEl = document.getElementById(`faq-a-${idx}`);
      const icon  = btn.querySelector('.lp-faq-chevron') as HTMLElement;
      const isOpen = ansEl?.style.display !== 'none';
      // Close all
      document.querySelectorAll('[id^="faq-a-"]').forEach(el => { (el as HTMLElement).style.display = 'none'; });
      document.querySelectorAll('.lp-faq-chevron').forEach(el => { (el as HTMLElement).style.transform = ''; });
      // Open this one if it was closed
      if (!isOpen && ansEl) {
        ansEl.style.display = 'block';
        if (icon) icon.style.transform = 'rotate(180deg)';
      }
    });
  });
}
